/**
 * SettingsPanel — Slide-over panel from right edge
 *
 * Sections: Subscription, Account, Legal, Support, Account Actions
 * All styling via T.* design tokens. No Tailwind. No hardcoded hex.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts';
import { useSubscription } from '@/hooks';
import { T } from '@/styles/tokens';
import { PREMIUM_PRICE } from '@/types/subscription';
import { version } from '../../package.json';
import {
  X,
  Copy,
  ChevronRight,
  Lock,
  Mail,
  HelpCircle,
  AlertCircle,
  LogOut,
  Trash2,
  Download,
  Loader2,
} from 'lucide-react';
import { DeleteAccountModal } from '@/components/ui/DeleteAccountModal';

// ── Types ───────────────────────────────────────────────────────

type DashboardMode = 'business' | 'everything';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  dashboardMode?: DashboardMode;
  onDashboardModeChange?: (mode: DashboardMode) => void;
}

// ── Shared Styles ───────────────────────────────────────────────

const S = {
  backdrop: (open: boolean): React.CSSProperties => ({
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    zIndex: 59,
    opacity: open ? 1 : 0,
    pointerEvents: open ? 'auto' : 'none',
    transition: 'opacity 280ms ease',
  }),

  panel: (open: boolean): React.CSSProperties => ({
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    maxWidth: '420px',
    backgroundColor: T.cream,
    zIndex: 60,
    transform: open ? 'translateX(0)' : 'translateX(100%)',
    transition: `transform 280ms ${T.easeOut}`,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: T.fontFamily,
  }),

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px 12px',
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)',
    borderBottom: `1px solid ${T.border}`,
  } as React.CSSProperties,

  title: {
    fontSize: '26px',
    fontWeight: 700,
    color: T.text,
    letterSpacing: '-0.03em',
    fontFamily: T.fontFamily,
    margin: 0,
  } as React.CSSProperties,

  closeBtn: {
    width: '36px',
    height: '36px',
    minWidth: '36px',
    minHeight: '36px',
    borderRadius: '50%',
    backgroundColor: T.borderLight,
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
  } as React.CSSProperties,

  scroll: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 20px calc(32px + env(safe-area-inset-bottom, 0px) + 3.5rem)',
    WebkitOverflowScrolling: 'touch',
  } as React.CSSProperties,

  sectionLabel: (first?: boolean): React.CSSProperties => ({
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: T.textSecondary,
    marginBottom: T.md,
    marginTop: first ? T['2xl'] : T['3xl'],
    fontFamily: T.fontFamily,
  }),

  card: {
    backgroundColor: T.white,
    borderRadius: T.radiusMd,
    border: `1px solid ${T.border}`,
    overflow: 'hidden',
  } as React.CSSProperties,

  cardPadded: {
    backgroundColor: T.white,
    borderRadius: T.radiusMd,
    border: `1px solid ${T.border}`,
    overflow: 'hidden',
    padding: '16px',
  } as React.CSSProperties,

  infoRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  } as React.CSSProperties,

  label: {
    fontSize: '13px',
    fontWeight: 500,
    color: T.textSecondary,
    fontFamily: T.fontFamily,
  } as React.CSSProperties,

  value: {
    fontSize: '13px',
    fontWeight: 500,
    color: T.text,
    fontFamily: T.fontFamily,
  } as React.CSSProperties,

  valueBold: {
    fontSize: '13px',
    fontWeight: 600,
    color: T.text,
    fontFamily: T.fontFamily,
    fontVariantNumeric: 'tabular-nums',
  } as React.CSSProperties,

  divider: {
    height: '1px',
    backgroundColor: T.borderLight,
    margin: 0,
  } as React.CSSProperties,

  listRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    background: 'none',
    border: 'none',
    width: '100%',
    cursor: 'pointer',
    fontFamily: T.fontFamily,
    minHeight: '44px',
    boxSizing: 'border-box',
    textDecoration: 'none',
  } as React.CSSProperties,

  listRowLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  } as React.CSSProperties,

  listRowText: {
    fontSize: '13px',
    fontWeight: 500,
    color: T.text,
    fontFamily: T.fontFamily,
  } as React.CSSProperties,
} as const;

// ── Badge ───────────────────────────────────────────────────────

type BadgeVariant = 'teal' | 'orange' | 'gray';

function badge(variant: BadgeVariant): React.CSSProperties {
  const m: Record<BadgeVariant, { bg: string; fg: string }> = {
    teal:   { bg: `rgba(61,171,165,0.1)`,  fg: '#1D7A75' },
    orange: { bg: `rgba(224,122,61,0.1)`,   fg: '#9A5228' },
    gray:   { bg: `rgba(168,165,160,0.1)`,  fg: T.textMuted },
  };
  const c = m[variant];
  return {
    fontSize: '11px',
    fontWeight: 600,
    color: c.fg,
    backgroundColor: c.bg,
    padding: '3px 10px',
    borderRadius: T.radiusPill,
    fontFamily: T.fontFamily,
    whiteSpace: 'nowrap',
  };
}

// ── Component ───────────────────────────────────────────────────

export function SettingsPanel({ isOpen, onClose, dashboardMode = 'business', onDashboardModeChange }: SettingsPanelProps) {
  const navigate = useNavigate();
  const { user, profile, signOut, resetPassword } = useAuth();
  const {
    isPremium,
    isTrialing,
    isCanceling,
    trialDaysRemaining,
    subscribe,
    openPortal,
    stripeCustomerId,
    currentPeriodEnd,
  } = useSubscription();

  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [subPage, setSubPage] = useState<'how-it-works' | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  // Reset sub-page when panel closes
  useEffect(() => {
    if (!isOpen) setSubPage(null);
  }, [isOpen]);

  // ── Helpers ─────────────────────────────────────────────────

  const notify = useCallback((msg: string, ok = true) => {
    clearTimeout(timerRef.current);
    setToast({ msg, ok });
    timerRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  // Escape closes
  useEffect(() => {
    if (!isOpen) return;
    const fn = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [isOpen, onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // ── Actions ─────────────────────────────────────────────────

  const doSignOut = async () => {
    try { await signOut(); onClose(); navigate('/signup'); }
    catch { notify('Unable to sign out. Try again.', false); }
  };

  const doSubscribe = async () => {
    setLoading(true);
    try { await subscribe(); }
    catch { notify('Unable to start subscription. Try again.', false); }
    finally { setLoading(false); }
  };

  const doManage = async () => {
    setLoading(true);
    try { await openPortal(); }
    catch { notify('Unable to open billing portal. Try again.', false); setLoading(false); }
  };

  const doCopyEmail = async () => {
    if (!user?.email) return;
    try { await navigator.clipboard.writeText(user.email); notify('Email copied'); }
    catch { notify('Failed to copy email', false); }
  };

  const doResetPw = async () => {
    if (!user?.email) return;
    setResetting(true);
    try { await resetPassword(user.email); notify(`Reset link sent to ${user.email}`); }
    catch { notify('Unable to send reset link. Try again.', false); }
    finally { setResetting(false); }
  };

  const doExport = () => {
    const e = user?.email || '';
    window.location.href = `mailto:support@trueprice.art?subject=Data%20Export%20Request&body=Please%20export%20my%20data%20for%20account%3A%20${encodeURIComponent(e)}`;
    notify('Opening email client…');
  };

  // ── Subscription display ────────────────────────────────────

  const fmtDate = (ts?: number | null) =>
    ts ? new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

  const fmtDateLong = (ts?: number | null) =>
    ts ? new Date(ts * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

  const sub = (() => {
    const expiring = isTrialing && trialDaysRemaining !== null && trialDaysRemaining <= 3;
    if (expiring) return {
      badge: 'Expiring', variant: 'orange' as BadgeVariant,
      plan: `Trial ends ${fmtDate(currentPeriodEnd)}`,
      cta: 'Subscribe now', action: doSubscribe,
    };
    if (isTrialing) return {
      badge: 'Trial', variant: 'teal' as BadgeVariant,
      plan: `7-day trial \u00b7 ends ${fmtDate(currentPeriodEnd)}`,
      cta: 'Manage subscription', action: doManage,
    };
    if (isPremium) return {
      badge: 'Active', variant: 'teal' as BadgeVariant,
      plan: `${PREMIUM_PRICE.name} \u00b7 ${PREMIUM_PRICE.display}`,
      cta: 'Manage subscription', action: doManage,
    };
    return {
      badge: 'Inactive', variant: 'gray' as BadgeVariant,
      plan: 'No active plan',
      cta: `Subscribe \u00b7 ${PREMIUM_PRICE.display}`, action: doSubscribe,
    };
  })();

  // ── Member since ────────────────────────────────────────────

  const memberSince = profile?.createdAt
    ? new Date(profile.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : null;

  // ── Render ──────────────────────────────────────────────────

  return createPortal(
    <>
      {/* Backdrop */}
      <div style={S.backdrop(isOpen)} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} />

      {/* Panel */}
      <div style={S.panel(isOpen)} role="dialog" aria-label="Settings">

        {/* Header */}
        <div style={S.header}>
          <h1 style={S.title}>Settings</h1>
          <button style={S.closeBtn} onClick={onClose} aria-label="Close settings">
            <X size={16} strokeWidth={2} color={T.text} />
          </button>
        </div>

        {/* Scroll area */}
        <div style={S.scroll}>

          {/* Toast */}
          {toast && (
            <div style={{
              padding: '10px 16px',
              margin: '16px 0 0',
              borderRadius: T.radiusSm,
              fontSize: '13px',
              fontWeight: 500,
              fontFamily: T.fontFamily,
              backgroundColor: toast.ok ? 'rgba(61,171,165,0.08)' : 'rgba(214,69,69,0.08)',
              color: toast.ok ? T.primary : T.danger,
              border: `1px solid ${toast.ok ? 'rgba(61,171,165,0.2)' : 'rgba(214,69,69,0.2)'}`,
            }}>
              {toast.msg}
            </div>
          )}

          {/* ── 0. DASHBOARD VIEW ────────────────────────── */}
          <div style={S.sectionLabel(true)}>Dashboard View</div>
          <div style={S.card}>
            {([
              { key: 'business' as DashboardMode, label: 'Art Business', desc: 'Art-related spending and income' },
              { key: 'everything' as DashboardMode, label: 'Everything', desc: 'Include personal spending in totals' },
            ]).map((opt, i) => (
              <div key={opt.key}>
                {i > 0 && <div style={S.divider} />}
                <button
                  onClick={() => onDashboardModeChange?.(opt.key)}
                  style={{
                    ...S.listRow,
                    backgroundColor: dashboardMode === opt.key ? 'rgba(61,171,165,0.05)' : 'transparent',
                    transition: 'background-color 150ms',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      border: `2px solid ${dashboardMode === opt.key ? T.primary : T.gray300}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'border-color 200ms',
                      flexShrink: 0,
                    }}>
                      {dashboardMode === opt.key && (
                        <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: T.primary }} />
                      )}
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{opt.label}</div>
                      <div style={{ fontSize: 11, color: T.gray400, marginTop: 1 }}>{opt.desc}</div>
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>

          {/* ── 1. SUBSCRIPTION ─────────────────────────── */}
          <div style={S.sectionLabel()}>Subscription</div>
          <div style={S.cardPadded}>
            <div style={S.infoRow}>
              <span style={S.label}>Status</span>
              <span style={badge(sub.variant)}>{sub.badge}</span>
            </div>
            <div style={{ ...S.infoRow, marginBottom: isPremium && !isTrialing && currentPeriodEnd ? 10 : 0 }}>
              <span style={S.label}>Plan</span>
              <span style={S.valueBold}>{sub.plan}</span>
            </div>
            {isPremium && !isTrialing && currentPeriodEnd && (
              <div style={{ ...S.infoRow, marginBottom: 0 }}>
                <span style={S.label}>{isCanceling ? 'Access until' : 'Renews'}</span>
                <span style={S.value}>{fmtDateLong(currentPeriodEnd)}</span>
              </div>
            )}
            <button
              onClick={sub.action}
              disabled={loading}
              style={{
                width: '100%',
                height: '44px',
                backgroundColor: T.primary,
                color: T.white,
                border: 'none',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: loading ? 'wait' : 'pointer',
                fontFamily: T.fontFamily,
                marginTop: '14px',
                opacity: loading ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {loading
                ? <><Loader2 size={16} strokeWidth={2} className="spin" />Loading...</>
                : sub.cta}
            </button>
          </div>

          {/* ── 2. ACCOUNT ──────────────────────────────── */}
          <div style={S.sectionLabel()}>Account</div>
          <div style={S.cardPadded}>
            <div style={S.infoRow}>
              <span style={S.label}>Email</span>
              <button
                onClick={doCopyEmail}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                <span style={{ ...S.value, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.email || '\u2014'}
                </span>
                {user?.email && <Copy size={14} strokeWidth={2} color={T.textMuted} />}
              </button>
            </div>
            {memberSince && (
              <div style={{ ...S.infoRow, marginBottom: 0 }}>
                <span style={S.label}>Member since</span>
                <span style={S.value}>{memberSince}</span>
              </div>
            )}
            <button
              onClick={doResetPw}
              disabled={!user?.email || resetting}
              style={{
                width: '100%',
                height: '40px',
                backgroundColor: 'transparent',
                color: T.text,
                border: `1px solid ${T.border}`,
                borderRadius: '10px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: resetting ? 'wait' : 'pointer',
                fontFamily: T.fontFamily,
                marginTop: '14px',
                opacity: (!user?.email || resetting) ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              {resetting
                ? <><Loader2 size={14} strokeWidth={2} className="spin" />Sending...</>
                : 'Reset password'}
            </button>
          </div>

          {/* ── 3. LEGAL ────────────────────────────────── */}
          <div style={S.sectionLabel()}>Legal</div>
          <div style={S.card}>
            <div style={{ ...S.listRow, cursor: 'default', opacity: 0.8 }}>
              <div style={S.listRowLeft}>
                <Lock size={16} strokeWidth={2} color={T.primary} />
                <span style={{ ...S.listRowText, color: T.textSecondary }}>Pricing data is private</span>
              </div>
            </div>
            <div style={S.divider} />
            <button onClick={() => { onClose(); navigate('/app/terms', { state: { from: 'settings' } }); }} style={S.listRow}>
              <span style={S.listRowText}>Terms</span>
              <ChevronRight size={14} strokeWidth={2} color={T.textMuted} />
            </button>
            <div style={S.divider} />
            <button onClick={() => { onClose(); navigate('/app/privacy', { state: { from: 'settings' } }); }} style={S.listRow}>
              <span style={S.listRowText}>Privacy</span>
              <ChevronRight size={14} strokeWidth={2} color={T.textMuted} />
            </button>
          </div>

          {/* ── 4. SUPPORT ──────────────────────────────── */}
          <div style={S.sectionLabel()}>Support</div>
          <div style={S.card}>
            <button onClick={() => { window.location.href = 'mailto:support@trueprice.art?subject=TruePrice%20Support'; notify('Opening email client…'); }} style={S.listRow}>
              <div style={S.listRowLeft}>
                <Mail size={16} strokeWidth={2} color={T.textSecondary} />
                <span style={S.listRowText}>Contact support</span>
              </div>
              <ChevronRight size={14} strokeWidth={2} color={T.textMuted} />
            </button>
            <div style={S.divider} />
            <button onClick={() => { window.location.href = 'mailto:support@trueprice.art?subject=Bug%20Report&body=Describe%20the%20bug%3A%0A%0ASteps%20to%20reproduce%3A%0A%0ADevice%2Fbrowser%3A%0A'; notify('Opening email client…'); }} style={S.listRow}>
              <div style={S.listRowLeft}>
                <AlertCircle size={16} strokeWidth={2} color={T.textSecondary} />
                <span style={S.listRowText}>Report a bug</span>
              </div>
              <ChevronRight size={14} strokeWidth={2} color={T.textMuted} />
            </button>
            <div style={S.divider} />
            <button onClick={() => setSubPage('how-it-works')} style={S.listRow}>
              <div style={S.listRowLeft}>
                <HelpCircle size={16} strokeWidth={2} color={T.textSecondary} />
                <span style={S.listRowText}>How it works</span>
              </div>
              <ChevronRight size={14} strokeWidth={2} color={T.textMuted} />
            </button>
            <div style={S.divider} />
            <div style={{ padding: '10px 16px' }}>
              <span style={{ fontSize: '11px', color: T.textMuted, fontFamily: T.fontFamily }}>
                TruePrice v{version}
              </span>
            </div>
          </div>

          {/* ── 5. ACCOUNT ACTIONS ──────────────────────── */}
          <div style={S.sectionLabel()}>Account actions</div>
          <div style={S.card}>
            <button onClick={() => setSignOutOpen(true)} style={S.listRow}>
              <div style={S.listRowLeft}>
                <LogOut size={16} strokeWidth={2} color={T.text} />
                <span style={S.listRowText}>Sign out</span>
              </div>
            </button>
            <div style={S.divider} />
            <button onClick={doExport} style={S.listRow}>
              <div style={S.listRowLeft}>
                <Download size={16} strokeWidth={2} color={T.text} />
                <span style={S.listRowText}>Request data export</span>
              </div>
              <ChevronRight size={14} strokeWidth={2} color={T.textMuted} />
            </button>
            <div style={S.divider} />
            <button onClick={() => setDeleteOpen(true)} style={S.listRow}>
              <div style={S.listRowLeft}>
                <Trash2 size={16} strokeWidth={2} color={T.danger} />
                <span style={{ ...S.listRowText, color: T.danger }}>Delete account</span>
              </div>
            </button>
          </div>

        </div>{/* end scroll */}
      </div>{/* end panel */}

      {/* How it works sub-panel — slides over settings */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          maxWidth: '420px',
          backgroundColor: T.cream,
          zIndex: 61,
          transform: subPage === 'how-it-works' ? 'translateX(0)' : 'translateX(100%)',
          transition: `transform 280ms ${T.easeOut}`,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: T.fontFamily,
        }}
        role="dialog"
        aria-label="How it works"
      >
        {/* Sub-panel header */}
        <div style={S.header}>
          <button
            onClick={() => setSubPage(null)}
            style={{
              ...S.closeBtn,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-label="Back to settings"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.text} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <h1 style={S.title}>How it works</h1>
          <div style={{ width: '36px' }} />
        </div>

        {/* Sub-panel content */}
        <div style={S.scroll}>
          <p style={{ fontSize: '13px', color: T.textSecondary, fontFamily: T.fontFamily, marginTop: T['2xl'], marginBottom: T['2xl'], lineHeight: 1.5 }}>
            TruePrice combines four pricing variables into a single defensible range.
          </p>

          {[
            { n: '1', t: 'What it costs you to survive', d: 'Your monthly costs divided by your available working hours. This produces your Your Hourly Rate \u2014 the minimum hourly rate at which you don\u2019t lose money.', f: 'Your Hourly Rate = Monthly Costs \u00F7 Painting Hours' },
            { n: '2', t: 'What it costs to make this piece', d: 'Your hours multiplied by your Your Hourly Rate, adjusted for complexity tier, plus material costs. This is your Your Minimum.', f: 'Floor = (Hours \u00D7 ST \u00D7 Fidelity + Materials) \u00D7 (1 + Complexity Buffer)' },
            { n: '3', t: 'What the market says it\u2019s worth', d: 'A price-per-square-inch rate calibrated to your medium, size, substrate, and career stage. This produces your Market High.', f: 'Ceiling = PSI \u00D7 Area \u00D7 Size \u00D7 Stage \u00D7 Substrate' },
            { n: '4', t: 'What your career stage supports', d: 'Your stated career stage is verified against your sale history. The Calibration Gate prevents self-selection bias.', f: 'Calibration = Highest Sale \u2265 Stage Threshold' },
          ].map((step, i, arr) => (
            <div key={step.n} style={{ position: 'relative', paddingLeft: '44px', marginBottom: i < arr.length - 1 ? '20px' : '0' }}>
              {/* Step circle */}
              <div style={{
                position: 'absolute', left: 0, top: 0, width: '32px', height: '32px',
                borderRadius: '50%', backgroundColor: T.primary,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '13px', fontWeight: 700, color: T.white, fontFamily: T.fontFamily,
              }}>{step.n}</div>

              {/* Connector */}
              {i < arr.length - 1 && (
                <div style={{
                  position: 'absolute', left: '15px', top: '32px', width: '2px',
                  height: 'calc(100% + 8px)', backgroundColor: 'rgba(61,171,165,0.2)',
                }} />
              )}

              <h3 style={{ fontSize: '14px', fontWeight: 600, color: T.text, fontFamily: T.fontFamily, margin: '0 0 4px' }}>
                {step.t}
              </h3>
              <p style={{ fontSize: '13px', color: T.textSecondary, fontFamily: T.fontFamily, lineHeight: 1.5, margin: '0 0 8px' }}>
                {step.d}
              </p>
              <div style={{
                display: 'inline-block', backgroundColor: T.borderLight,
                padding: '4px 10px', borderRadius: T.radiusSm,
              }}>
                <code style={{ fontSize: '11px', color: T.textSecondary, fontFamily: "'Manrope', monospace" }}>
                  {step.f}
                </code>
              </div>
            </div>
          ))}

          {/* Philosophy */}
          <div style={{
            marginTop: '24px', padding: '16px',
            backgroundColor: 'rgba(61,171,165,0.06)',
            borderRadius: T.radiusMd,
            border: '1px solid rgba(61,171,165,0.12)',
          }}>
            <p style={{ fontSize: '13px', color: T.primary, fontFamily: T.fontFamily, lineHeight: 1.5, margin: 0 }}>
              <span style={{ fontWeight: 600 }}>The TruePrice Philosophy:</span> Your floor isn't about what the market will pay — it's about what you need to survive as an artist. Price below your floor, and you're paying the client to take your work.
            </p>
          </div>

          {/* Contact */}
          <div style={{ marginTop: '24px', textAlign: 'center' }}>
            <p style={{ fontSize: '13px', color: T.textSecondary, fontFamily: T.fontFamily, margin: 0 }}>
              Questions? Contact us at{' '}
              <a href="mailto:support@trueprice.art" style={{ color: T.primary, textDecoration: 'none' }}>
                support@trueprice.art
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Sign out confirm modal */}
      {signOutOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 70,
            backgroundColor: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px',
          }}
          onClick={() => setSignOutOpen(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: T.white,
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '340px',
              width: '100%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }}
          >
            <h2 style={{
              fontSize: '18px', fontWeight: 600, color: T.text,
              marginBottom: '8px', fontFamily: T.fontFamily, margin: '0 0 8px',
            }}>Sign out?</h2>
            <p style={{
              fontSize: '14px', color: T.textSecondary, lineHeight: 1.5,
              marginBottom: '24px', fontFamily: T.fontFamily,
            }}>
              You'll need to sign back in to access your pricing data.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setSignOutOpen(false)}
                style={{
                  flex: 1, height: '44px', borderRadius: '10px',
                  fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  fontFamily: T.fontFamily,
                  backgroundColor: 'transparent',
                  color: T.text,
                  border: `1px solid ${T.border}`,
                }}
              >Cancel</button>
              <button
                onClick={() => { setSignOutOpen(false); doSignOut(); }}
                style={{
                  flex: 1, height: '44px', borderRadius: '10px',
                  fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  fontFamily: T.fontFamily,
                  backgroundColor: T.primary,
                  color: T.white,
                  border: 'none',
                }}
              >Sign out</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      <DeleteAccountModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onExportFirst={doExport}
      />

      {/* Spinner animation */}
      <style>{`
        .spin { animation: _spin 1s linear infinite; }
        @keyframes _spin { to { transform: rotate(360deg); } }
      `}</style>
    </>,
    document.body
  );
}

export default SettingsPanel;
